/*
 * COPYRIGHT (c) 2022
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { Button } from '@scuf/common';
import ChartWidgetPortfolio from '../../../shared/Chart/ChartWidgetPortfolio';
import {
  convertDataForCarbonSavingsMeter,
  graphCarbon
} from '../../../shared/Utils/Utils';
import ProgressBarWidget from '../../../shared/ProgressBar/ProgressBarWidget';
import { ICarbonData } from '../../IEnergyManagementProps';
import SavingsMeterWidget from '../../../shared/SavingsMeter/SavingsMeterWidget';
import { ApiContext } from '../../../../context/ApiContext';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import { getProgressBarUnitScaledData } from '../../../shared/Utils/portfolioDataFormatter';
import { IGrapghData } from '../../../shared/Chart/IChartWidgetProps';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import { PERCENT_CHANGE_THRESHOLD } from '../../../shared/Utils/Constants';
import { getMaxConvertedValueFromArray } from '../../../shared/Utils/unitHandling/unitHandling';

interface ICarbonComponentProps {
  data: ICarbonData;
  toggleFeatureScopeOne: boolean | undefined;
  forGraph?: boolean;
  forSummary?: boolean;
  forTable?: boolean;
  checkAllsitesAndMonthData?: boolean;
  dataSourceType?: string;
}

const CarbonComponentSeparateSummary = (props: ICarbonComponentProps) => {
  const { data, checkAllsitesAndMonthData } = props;

  const apiContext = useContext(ApiContext);

  const progressBarUnitScaledData = getProgressBarUnitScaledData(
    ConversionType.CARBON,
    {
      previousPeriod: data.totalAggregations.totalPreviousPeriodEmission,
      actual: data.totalAggregations.totalActualEmission
    },
    data.totalAggregations.totalDeviation,
    data.emissionMeasurementUnit,
    undefined,
    null,
    null,
    undefined,
    apiContext.showChangeWhenData,
    true
  );
  const aggregationCarbonEmissionData = {
    originalActual: data.totalAggregations.totalActualEmission,
    originalPreviousPeriod: data.totalAggregations.totalPreviousPeriodEmission,
    originalSavings: data.totalAggregations.totalDeviation,
    actual: progressBarUnitScaledData?.actual,
    previousPeriod: progressBarUnitScaledData?.previousPeriod,
    actualUnit: progressBarUnitScaledData?.actualUnit,
    baselineUnit: progressBarUnitScaledData?.baselineUnit,
    changeUnit: progressBarUnitScaledData?.changeUnit,
    savings:
      progressBarUnitScaledData?.deviation !== null &&
      Math.abs(Number(data.totalAggregations.totalDeviationPercentage)) <=
        PERCENT_CHANGE_THRESHOLD &&
      checkAllsitesAndMonthData
        ? progressBarUnitScaledData?.deviation
        : apiContext.showChangeWhenData
        ? null
        : progressBarUnitScaledData?.deviation,
    savingsPercentage:
      progressBarUnitScaledData?.deviation === null ||
      !checkAllsitesAndMonthData
        ? null
        : data.totalAggregations.totalDeviationPercentage
  };

  return (
    <div className="kpi-card-progress-bar-container">
      <ProgressBarWidget
        category="Energy"
        aggregationData={aggregationCarbonEmissionData}
        renderedFromCarbon={true}
        fromCarbonKPI={true}
        originalUnit={data.emissionMeasurementUnit}
        conversionType={ConversionType.CARBON}
      />
    </div>
  );
};

const CarbonComponentSeparateGraph = (props: ICarbonComponentProps) => {
  const [t, lng] = useTranslation();
  const d = descriptors.energyManagement;
  const { data } = props;

  const apiContext = useContext(ApiContext);
  const { onCarbonViewDetailsClick } = apiContext;

  const onViewDetailsClick = () => {
    if (onCarbonViewDetailsClick) {
      onCarbonViewDetailsClick();
    }
  };

  const { Unit: displayUnit } = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    data.emissionMeasurementUnit,
    data.monthWiseAggregations.map((item) => item.totalActualEmission)
  );
  const graphData = graphCarbon(
    data.monthWiseAggregations,
    data.emissionMeasurementUnit,
    displayUnit,
    data.totalSiteCount,
    lng
  );
  const YearIndex = graphData?.YearIndex;
  const MonthIndex = graphData?.MonthIndex;

  let first: string | undefined;
  let last: string | undefined;
  let previousyear: string | undefined;
  let currentyear: string | undefined;
  // let { 0: first, [MonthIndex.length - 1]: last } = MonthIndex;
  // let { 0: previousyear, [YearIndex.length - 1]: currentyear } = YearIndex;
  if (MonthIndex.length > 0) {
    first = MonthIndex[0]; // First element
    last = MonthIndex[MonthIndex.length - 1]; // Last element
  }

  if (YearIndex.length > 0) {
    previousyear = YearIndex[0]; // First element
    currentyear = YearIndex[YearIndex.length - 1]; // Last element
  }
  return (
    <div className="kpi-card-monthwise-container">
      {previousyear != currentyear ? (
        <div className="kpi-card-monthwise-date-range">
          {t(d.CARBONKPIDETAILS)} {first} {previousyear} - {last} {currentyear}
        </div>
      ) : (
        <div className="kpi-card-monthwise-date-range">
          {t(d.CARBONKPIDETAILS)} {first} - {last} {currentyear}
        </div>
      )}
      <ChartWidgetPortfolio
        graphData={graphData as IGrapghData}
        unit={displayUnit}
        axisUnitType="consumption"
        renderedFromCarbon={true}
        showAllTooltips={true}
        dataSourceType={props.dataSourceType}
      />
      <div className="kpi-card-view-details">
        <Button
          type="link"
          content={t(d.VIEWDETAILS)}
          className="view-details-button"
          onClick={() => onViewDetailsClick()}
        />
      </div>
    </div>
  );
};

const CarbonComponentSeparateTable = (props: ICarbonComponentProps) => {
  const { data, checkAllsitesAndMonthData } = props;
  const apiContext = useContext(ApiContext);

  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  let consumptionMeterData = props.toggleFeatureScopeOne
    ? convertDataForCarbonSavingsMeter(
        data.emissionsByScope,
        data.emissionMeasurementUnit,
        props.toggleFeatureScopeOne,
        apiContext.showChangeWhenData
      )
    : convertDataForCarbonSavingsMeter(
        data.emissionsByUtility,
        data.emissionMeasurementUnit,
        false,
        apiContext.showChangeWhenData
      );

  const { values, displayUnit } = consumptionMeterData;

  const [consumptionType, setconsumptionType] = useState<string>(
    t(d.HIGHWITHOUTUNIT)
  );

  const headers = [
    {
      HeaderValue: t(d.CARBONSAVINGSMETERHEADER),
      TextAlignValue: 'left',
      Field: 'ConsumptionTypeName'
    },
    {
      // HeaderValue: consumptionType,
      HeaderValue: t(d.CARBONNOUNIT),
      TextAlignValue: 'right',
      Field: 'ConsumptionHigh',
      sortable: true,
      initialWidth: '15%'
    },
    {
      HeaderValue: t(d.CHANGE),
      TextAlignValue: 'right',
      Field: 'ConsumptionDeviation',
      sortable: true,
      initialWidth: '25%'
    }
  ];
  const onSortingHandler = (sortOrder: number): void => {
    sortOrder === 1
      ? setconsumptionType(t(d.LOWWITHNOUNIT))
      : setconsumptionType(t(d.HIGHWITHOUTUNIT));
  };
  return (
    <div className="kpi-card-meter-container">
      <SavingsMeterWidget
        columnHeaders={headers as IDataTableHeader[]}
        meterData={values}
        onSorting={onSortingHandler}
        toggleFeatureScopeOne={props.toggleFeatureScopeOne}
        emissionMeasurementUnit={data.emissionMeasurementUnit}
        hideChange={!checkAllsitesAndMonthData}
      />
    </div>
  );
};

export const CarbonComponentSeparate = (props: ICarbonComponentProps) => {
  return (
    <div className="kpi-card-flex-container">
      {props.forSummary && <CarbonComponentSeparateSummary {...props} />}
      {props.forGraph && <CarbonComponentSeparateGraph {...props} />}
      {props.forTable && <CarbonComponentSeparateTable {...props} />}
    </div>
  );
};
export default memo(CarbonComponentSeparate);
